<template>
  <!-- 众包结算订单详情 -> 任务信息 -->
  <div class="base-card-info">
    <div class="info-card-title">
      任务信息
    </div>
    <div class="custom-base-info">
      <!-- 任务名称 -->
      <div class="item">
        <div class="label">
          任务名称
        </div>
        <div class="value">
          {{ duty.dutyName || '--' }}
        </div>
      </div>
      <!-- 收入性质 -->
      <div v-if="!isHnFreePartners" class="item">
        <div class="label">
          收入性质
        </div>
        <div class="value">
          {{ proxyPayTypeConfig[duty.proxyPayType] || '--' }}
        </div>
      </div>
      <!-- 开票方式 -->
      <div class="item">
        <div class="label">
          开票方式
        </div>
        <div class="value">
          {{ invoiceTaxMethodConfig[duty.invoiceTaxMethod] || '--' }}
        </div>
      </div>
      <!-- 个人发票税源地 -->
      <div v-if="isSaas || isServer" class="item">
        <div class="label">
          个人发票税源地
        </div>
        <div class="value">
          {{ personalInvoiceTaxSourceLocationLabel[duty.taxSourceType] || '--' }}
        </div>
      </div>
      <!-- 订单编号 -->
      <div class="item">
        <div class="label">
          订单编号
        </div>
        <div class="value">
          {{ duty.orderNo || '--' }}
        </div>
      </div>
      <!-- 订单状态 -->
      <div class="item">
        <div class="label">
          订单状态
        </div>
        <div class="value">
          <custom-tag
            v-if="!isNull(duty.orderStatus)"
            class="!justify-start"
            :type="orderStatus"
            :label="settlementStatusLabel[duty.orderStatus]"
          />
          <span v-else>
            --
          </span>
        </div>
      </div>
      <!-- 任务类型 -->
      <div class="item">
        <div class="label">
          任务类型
        </div>
        <div class="value">
          {{ duty.dutyTempName || '--' }}
        </div>
      </div>
      <!-- 订单开始时间 -->
      <div class="item">
        <div class="label">
          订单开始时间
        </div>
        <div class="value">
          {{ duty.startTime || '--' }}
        </div>
      </div>
      <!-- 订单结束时间 -->
      <div class="item">
        <div class="label">
          订单结束时间
        </div>
        <div class="value">
          {{ duty.endTime || '--' }}
        </div>
      </div>
      <!-- 企业名称 -->
      <div class="item">
        <div class="label">
          企业名称
        </div>
        <div class="value">
          {{ duty.companyName || '--' }}
        </div>
      </div>
      <!-- 开票科目 -->
      <div class="item">
        <div class="label">
          开票科目
        </div>
        <div class="value">
          {{ duty.invoiceSubName || '--' }}
        </div>
      </div>
      <!-- 支付通道 -->
      <div class="item">
        <div class="label">
          支付通道
        </div>
        <div class="value">
          {{ paymentChannelLabel[duty.payType] || '--' }}
        </div>
      </div>
      <!-- 服务主体 -->
      <div class="item">
        <div class="label">
          服务主体
        </div>
        <div class="value">
          {{ duty.serverName || '--' }}
        </div>
      </div>
      <!-- 收款方式 -->
      <div class="item">
        <div class="label">
          收款方式
        </div>
        <div class="value">
          {{ info?.receivedType === 1 ? '支付宝' : info?.receivedType === 0 ? '银行卡' : '--' }}
        </div>
      </div>
      <!-- 任务合同 -->
      <div class="item">
        <div class="label">
          任务合同
        </div>
        <div class="value">
          <el-button v-if="duty.contractUrl" link @click="openLinkFile(duty.contractUrl)">
            {{ duty.contractName || '--' }}
          </el-button>
          <span v-else>
            --
          </span>
        </div>
      </div>
      <!-- 订单合同 -->
      <div class="item">
        <div class="label">
          订单合同
        </div>
        <div class="value">
          <el-button v-if="info.contractUrl" link @click="openLinkFile(info.contractUrl)">
            {{ info.contractName || '--' }}
          </el-button>
          <span v-else>
            --
          </span>
        </div>
      </div>
      <template v-if="!isSaas && !isServer">
        <!-- 空盒子 -->
        <!-- <div class="item">
          <div class="label">
          &nbsp;
          </div>
          <div class="value">
          &nbsp;
          </div>
        </div> -->
      </template>
    </div>

    <div class="custom-base-info pt-3">
      <!-- 结算说明 -->
      <div class="item">
        <div class="label">
          结算说明
        </div>
        <div class="value !max-w-[760px]">
          {{ duty.remark || '--' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CustomTag } from '@vue3/components';
import { PropType, computed } from 'vue';
import { ElMessage } from 'element-plus';
import { downloadLinkFile } from '@core/api';
import {
  ISettlementOrderDetail,
  settlementStatusStyle,
  ISettlementOrderDuty,
  paymentChannelLabel,
  settlementStatusLabel,
  proxyPayTypeConfig,
  personalInvoiceTaxSourceLocationLabel,
  invoiceTaxMethodConfig,
  serverCrowdCreditCode, 
} from '@core/main/types';
import { isNull } from '@core/tools';
import { useUserStore } from '@core/main';

const { state: userState } = useUserStore();

const props = defineProps({
  info: {
    type: Object as PropType<ISettlementOrderDetail>,
    required: true,
  },
  duty: {
    type: Object as PropType<ISettlementOrderDuty>,
    required: true,
  },
  term: {
    type: String as PropType<'company' | 'agent' | 'saas' | 'server'>,
    required: true,
  },
});

const isSaas = computed(() => props.term === 'saas');
const isServer = computed(() => props.term === 'server');
const isHnFreePartners = computed(() => serverCrowdCreditCode?.includes(userState.identity?.serverDTO?.creditCode) && isServer.value);

const orderStatus = computed(() => settlementStatusStyle[props.duty.orderStatus] as any);

async function openLinkFile(url: string) {
  try {
    await downloadLinkFile(url);
    ElMessage.success('下载成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}
</script>

<style lang="scss" scoped>
.info-item-label {
  @apply text-[var(--color-text-desc)] pr-4 flex gap-1;

  .svg-icon {
    position: relative;
    top: 2px;
  }
}

.info-item-value {
  @apply text-[var(--color-text-primary)] flex-1;
}

.h-icon {
  @apply text-[var(--color-primary)] ml-1;

  width: 16px !important;
  height: 16px !important;
}

.custom-base-info .custom-tag .label {
  @apply text-[var(--color-primary)];
}
</style>
